import { useMemo, useState } from "react";
import Button from "@/components/Button";
import { useMutation } from "@apollo/client";
import { Controller, useForm, useWatch } from "react-hook-form";
import { UPDATE_PERSON_MUTATION } from "@/features/person/queries";
import TextField from "@/components/TextField";
import { toInput } from "../../../utils/utils";
import { isLastNameCasingOnlyChange } from "../../../utils/lastName";
import FileUpload from "@/components/FileUpload/FileUpload";
import Icon from "@/components/Icon";
import SubSectionHeading from "@/components/SubSectionHeading/SubSectionHeading";
import Divider from "@/components/Divider";
import { Person } from "@/types";

type FieldValues = {
  firstName: string;
  lastName?: string;
  middleName?: string;
  suffixName?: string;
  nameChangeDocumentName?: string;
};

interface OfficialEditFormProps {
  personId: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  suffixName?: string;
  canEditLastName?: boolean;
  onCancel(): void;
  onSuccess(updatedPerson: Partial<Person>, lastNameChanged?: boolean): void;
  onError(error: string): void;
}

const defaultErrorMessage = "Failed to update official information";

export default function OfficialEditForm({
  personId,
  firstName = "",
  lastName = "",
  middleName = "",
  suffixName = "",
  canEditLastName = false,
  onCancel,
  onSuccess,
  onError
}: OfficialEditFormProps) {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string>("");
  const [documentNameError, setDocumentNameError] = useState<string>("");

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, isValid }
  } = useForm({
    defaultValues: {
      firstName,
      lastName,
      middleName,
      suffixName,
      nameChangeDocumentName: ""
    },
    mode: "onChange"
  });

  const watchedLastName = useWatch({ control, name: "lastName" });
  const lastNameChanged = watchedLastName !== lastName;
  const requiresNameChangeDocument = lastNameChanged && !isLastNameCasingOnlyChange(lastName, watchedLastName ?? "");

  const [updatePerson, { loading }] = useMutation(UPDATE_PERSON_MUTATION);

  const handleFileUpload = (file: File) => {
    setUploadError("");
    setDocumentNameError("");
    setUploadedFile(file);
    setValue("nameChangeDocumentName", file.name);
  };

  const handleFileError = (error: string) => {
    setUploadError(error);
    setUploadedFile(null);
  };

  async function onSubmit(values: FieldValues) {
    if (requiresNameChangeDocument && canEditLastName) {
      const missingFile = !uploadedFile;
      const missingDocName = !values.nameChangeDocumentName?.trim();

      if (missingFile) setUploadError("Proof of name change document is required");
      if (missingDocName) setDocumentNameError("Document Name is required");

      if (missingFile || missingDocName) return;
    }

    try {
      const input = toInput({
        id: personId,
        firstName: values.firstName,
        middleName: values.middleName,
        suffixName: values.suffixName,
        ...(canEditLastName && { lastName: values.lastName }),
        ...(requiresNameChangeDocument &&
          uploadedFile && {
            nameChangeDocument: uploadedFile,
            nameChangeDocumentName: values.nameChangeDocumentName
          })
      });

      const { data } = await updatePerson({
        variables: { input }
      });

      const {
        updatePerson: { success, person, message }
      } = data;

      if (success) {
        return onSuccess(person, lastNameChanged);
      }

      return onError(message || defaultErrorMessage);
    } catch (error) {
      return onError((error as Error).message || defaultErrorMessage);
    }
  }

  const isSaveDisabled = useMemo(() => {
    if (loading || !isValid) return true;
    return false;
  }, [loading, isValid]);

  const lastNameSupportingText = useMemo(() => {
    if (!canEditLastName) {
      return "You do not have permission to edit last name";
    }
    return undefined;
  }, [canEditLastName]);

  return (
    <div className="flex flex-col gap-6" data-testid="official-edit-form">
      <Controller
        control={control}
        name="firstName"
        rules={{
          required: "First Name is required"
        }}
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value ?? ""}
            label="Legal First Name *"
            error={!!errors.firstName}
            errorText={errors.firstName?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="middleName"
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value ?? ""}
            label="Middle Initial"
            error={!!errors.middleName}
            errorText={errors.middleName?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="lastName"
        rules={{
          required: "Last Name is required"
        }}
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value ?? ""}
            label="Legal Last Name *"
            error={!!errors.lastName}
            errorText={errors.lastName?.message}
            supportingText={lastNameSupportingText}
            disabled={!canEditLastName}
          />
        )}
      />

      {canEditLastName && requiresNameChangeDocument && (
        <>
          <Divider />
          <div className="flex flex-col gap-3">
            <SubSectionHeading>Required: Upload Proof of Name Change</SubSectionHeading>
            {uploadedFile ? (
              <div className="border border-solid border-sys-brand-outline rounded px-4 py-3 flex items-center gap-3">
                <div className="flex-1">
                  <p className="text-sys-brand-on-surface text-body-large">{uploadedFile.name}</p>
                  <p className="text-sys-brand-on-surface-variant text-body-medium">Upload complete</p>
                </div>
                <Icon name="check_circle" className="text-extended-success-brand-color" size={24} />
              </div>
            ) : (
              <FileUpload
                label="Choose a File to Upload"
                subtitle="Allowed file extensions: pdf, jpg, png"
                accept=".pdf,.jpg,.png"
                handleUpload={handleFileUpload}
                errorMsg={uploadError}
                onError={handleFileError}
              />
            )}
          </div>

          <Controller
            control={control}
            name="nameChangeDocumentName"
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                onChange={(val) => {
                  field.onChange(val);
                  if (documentNameError) setDocumentNameError("");
                }}
                label="Document Name *"
                error={!!documentNameError}
                errorText={documentNameError}
              />
            )}
          />
          <Divider />
        </>
      )}

      <Controller
        control={control}
        name="suffixName"
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value ?? ""}
            label="Name Suffix"
            supportingText="Examples: Jr., Sr., II, III"
            error={!!errors.suffixName}
            errorText={errors.suffixName?.message}
          />
        )}
      />

      <div className="flex flex-wrap justify-end gap-1 items-center">
        <Button variant="text" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={isSaveDisabled} onClick={handleSubmit(onSubmit)}>
          Save
        </Button>
      </div>
    </div>
  );
}
